import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import partition from 'lodash/partition';
import pick from 'lodash/pick';
import {Component} from 'panel';

import {apiHost} from '../../report/util';
import {emitTrackEvent} from '../../util/dom';
import {filterAttributes, pluralize} from '../../util/index';
import {apiClient} from '../../util/api-client';
import {registerMPElement} from '../../util/register-element';
import {DisplayContext as PropertyFilterDisplayContext, ResourceType} from '../property-filter-menu/models/types';
import template from './custom-event-modal-internal.jade';
import css from './custom-event-modal-internal.styl?inline';
import MPCustomEvent from './models/custom-event';
import EventSelector from './models/event-selector';
import {ELEMENTS_BY_CSS_CLASS, getResolvedCustomEvents} from './util/index';
import {deleteCustomEvent, saveCustomEvent} from './util/api';

const MAX_NAME_LENGTH = 255;
const DISPATCHED_EVENTS = {
  MODAL_CLOSED: `modalClosed`,
  CANCEL_EDITING: `cancelEditingCustomEvent`,
  DELETED: `deletedCustomEvent`,
  DELETING: `deletingCustomEvent`,
  SAVING: `savingCustomEvent`,
  SAVED: `savedCustomEvent`,
};

export default registerMPElement(
  `mp-custom-event-modal-internal`,
  class CustomEventModalInternal extends Component {
    static get attrsSchema() {
      return {
        ...super.attrsSchema,
        events: `json`,
        'is-blocked-people': `boolean`,
        open: `boolean`,
        'project-has-people': `boolean`,
        'project-id': `string`,
        'selected-custom-event-id': `number`,
        'workspace-id': `number`,
        'all-data-groups': `boolean`,
      };
    }
    constructor() {
      super();
      this.debouncedGetAffectedMessages = debounce(this.getAffectedMessages, 50);
    }

    static get dispatchedEvents() {
      return Object.values(DISPATCHED_EVENTS);
    }

    attributeChangedCallback(attr, oldVal, newVal) {
      super.attributeChangedCallback(attr, oldVal, newVal);
      switch (attr) {
        case `is-blocked-people`:
          this.update({isBlockedPeople: this.attr(attr)});
          break;
        case `project-has-people`:
          this.update({projectHasPeople: this.attr(attr)});
          break;
        case `project-id`:
          this.update({projectId: this.attr(attr)});
          break;
        case `events`: {
          const [customEvents, events] = partition(this.attr(attr), `custom`);
          this._customEvents = getResolvedCustomEvents(customEvents);
          this.update({events: [...events, ...this._customEvents]});
          break;
        }
        case `selected-custom-event-id`: {
          if (this.attr(attr)) {
            const customEventId = this.attr(attr);
            const selectedCustomEvent = this.customEvents.find((customEvent) => customEvent.id === customEventId);
            this.update({stagedCustomEvent: selectedCustomEvent});
          } else {
            this.clearStagedCustomEvent();
          }
          break;
        }
        case `open`: {
          const open = this.attr(attr);
          if (open) {
            this._bodyOverflowX = document.body.style.overflowX;
            document.body.style.overflowX = `hidden`;
          }
          this.update({open});
          break;
        }
        default:
          break;
      }
      if (
        this.initialized &&
        this.attr(`selected-custom-event-id`) &&
        [`workspace-id`, `project-id`, `selected-custom-event-id`].includes(attr)
      ) {
        this.debouncedGetAffectedMessages(this.attr(`selected-custom-event-id`));
      }
    }

    connectedCallback() {
      super.connectedCallback(...arguments);
      this._parentPositions = {};
    }

    get config() {
      return {
        template,
        css,
        useShadowDom: true,
        defaultState: {
          [this.builderStateReference]: this.defaultBuilderState,
          error: {},
          open: false,
          showDeleteModal: false,
          stagedCustomEvent: new MPCustomEvent(),
        },
        helpers: {
          ELEMENTS_BY_CSS_CLASS,
          MAX_NAME_LENGTH,
          PropertyFilterDisplayContext,
          pick,
          pluralize,
          filterAttributes,
          handleModalChange: ({detail}) => {
            if (detail && detail.state === `open`) {
              // Focus name input field on initial modal open when creating a new custom event.
              if (this.nameInputEl && !this.attr(`selected-custom-event-id`)) {
                this.nameInputEl.focus();
              }
            } else if (detail && detail.state === `closed`) {
              this.update({open: false});
              requestAnimationFrame(() => {
                this.dispatchEvent(new CustomEvent(DISPATCHED_EVENTS.MODAL_CLOSED));
                // clear errors and other transient state when modal closes
                this.update(pick(this.config.defaultState, [`showDeleteModal`, `error`]));
                document.body.style.overflowX = this._bodyOverflowX;
              });
            }
          },
          handleErrorModalChange: (ev) => {
            if (ev.detail && ev.detail.state === `closed`) {
              this.update({error: {}});
            }
          },

          //#region Modal styling

          /**
           * Normally would use style overrides, but getting overrides to work across
           * browsers that do and do not encapsulate styles is more brittle than
           * explicitly inlining the styles on the elements within the modal. This is in large
           * part due to the unusual situation of nesting a modal within a modal here.
           */
          styleDeleteCustomEventModal: (old, vnode) => {
            if (!this.styledDeleteCustomEventModal) {
              const {shadowRoot} = vnode.elm;
              const modalMain = shadowRoot.querySelector(`.mp-modal-main`);
              if (modalMain) {
                this.styledDeleteCustomEventModal = true;
                modalMain.style.position = `absolute`;
                const modalStage = shadowRoot.querySelector(`.mp-modal-stage`);
                Object.assign(modalStage.style, {
                  'min-height': `calc(100% + 2px)`,
                  'min-width': `calc(100% + 2px)`,
                  left: `-1px`,
                  top: `-1px`,
                });
                [...shadowRoot.querySelectorAll(`.mp-modal-stage, .mp-modal-wrapper, .mp-modal-backdrop`)].forEach(
                  (elm) => (elm.style.position = `absolute`),
                );
              }
            }
          },
          //#endregion

          getEventEntries: () => {
            const {stagedCustomEvent} = this.state;
            return stagedCustomEvent.eventSelectors.map((eventSelector) => ({
              event: {
                custom: eventSelector.eventIsCustom(),
                label: eventSelector.getEventLabel(),
                value: eventSelector.getEventName(),
                ...(eventSelector.eventIsCustom()
                  ? {
                      id: eventSelector.getEventId(),
                    }
                  : {}),
              },
              filters: eventSelector.propertyFilters.map((propertyFilter) => propertyFilter.getAttributes()),
              filtersOperator: eventSelector.filtersOperator,
              type: ResourceType.Event,
            }));
          },

          updateQueryEvents: (ev) => {
            const {stagedCustomEvent} = this.state;
            stagedCustomEvent.eventSelectors = ev.detail.eventEntries.map(
              ({filters, event, filtersOperator}) =>
                new EventSelector({
                  propertyFilters: filters,
                  event: event.custom ? {name: event.label, id: event.id} : event.value,
                  filtersOperator,
                }),
            );
            this.update({stagedCustomEvent});
          },

          captureTracking: (ev) => {
            const {eventName, props} = ev.detail;
            ev.stopPropagation();
            this.track(eventName, props || {});
          },

          cancelChanges: () => {
            this.track(`Cancel custom event modal`);
            this.dispatchEvent(new CustomEvent(DISPATCHED_EVENTS.CANCEL_EDITING));
          },
          isNewCustomEvent: () => !this.state.stagedCustomEvent.id,
          clearNameErrors: () => this.update({error: {}}),
          clickSave: () => {
            let {name, id} = this.state.stagedCustomEvent;
            name = name.trim();
            const allNames = this.customEvents
              .filter((customEvent) => customEvent.id !== id)
              .map((customEvent) => customEvent.name);
            if (!name) {
              this.update({error: {invalidName: true}});
            } else if (allNames.includes(name)) {
              this.update({error: {duplicateName: true}});
            } else {
              this.saveChanges();
            }
          },
          nameInputInserted: ({elm}) => {
            this.nameInputEl = elm;
          },
          updateCustomEventName: (ev) => {
            const stagedCustomEvent = this.state.stagedCustomEvent.updateName(ev.target.value);
            this.update({stagedCustomEvent});
          },
          clickDeleteCustomEvent: () => {
            this.update({showDeleteModal: true});
          },
          confirmDeleteCustomEvent: () => {
            this.deleteCustomEvent(this.state.stagedCustomEvent.id);
          },
          cancelDeleteCustomEvent: () => {
            this.update({showDeleteModal: false});
          },
        },
      };
    }

    async deleteCustomEvent(id) {
      const {projectId, affectedMessageIds} = this.state;
      this.track(`Delete custom event`);
      this.dispatchEvent(new CustomEvent(DISPATCHED_EVENTS.DELETING));
      try {
        if (!isEmpty(affectedMessageIds)) {
          await this.pauseMessages(affectedMessageIds);
        }

        await deleteCustomEvent(id, {projectId, workspaceId: this.attr(`workspace-id`)});
        this.dispatchEvent(new CustomEvent(DISPATCHED_EVENTS.DELETED, {detail: {id}}));
      } catch (err) {
        this.update({error: {deleteCustomEvent: true}, showDeleteModal: false, message: err.message});
        console.error(err);
      }
    }

    async saveChanges() {
      const {stagedCustomEvent, projectId, affectedMessageIds} = this.state;
      const trackingEvent = stagedCustomEvent.id ? `Update custom event` : `Save new custom event`;
      this.track(trackingEvent);
      this.dispatchEvent(new CustomEvent(DISPATCHED_EVENTS.SAVING));
      try {
        if (!isEmpty(affectedMessageIds)) {
          await this.pauseMessages(affectedMessageIds);
        }

        const result = await saveCustomEvent(stagedCustomEvent, {projectId, workspaceId: this.attr(`workspace-id`)});
        this.dispatchEvent(new CustomEvent(DISPATCHED_EVENTS.SAVED, {detail: {customEvent: result.custom_event}}));

        this.clearStagedCustomEvent();
      } catch (err) {
        this.update({error: {saveCustomEvent: true, message: err.message}});
        console.error(err);
      }
    }

    clearStagedCustomEvent() {
      this.update({stagedCustomEvent: new MPCustomEvent(), affectedMessageIds: []});
    }

    get customEvents() {
      return this._customEvents;
    }

    track(eventName, props = {}) {
      emitTrackEvent(this, {
        eventName,
        source: `mp-custom-event-modal`,
        props: {
          ...props,
          ...this.state.stagedCustomEvent.toTrackingData(),
        },
      });
    }

    //#region Builder methods

    get builderStateReference() {
      return `builder`;
    }

    get defaultBuilderState() {
      return {
        activeIndex: 0,
        panes: [],
        propertyFilterIndex: null,
        searchFilter: ``,
        selectorIndex: null,
      };
    }

    //#endregion

    //#region Notifications methods
    async pauseMessages(messageIds) {
      const {projectId} = this.state;
      const workspaceIdOrProjectId = this.attr(`workspace-id`)
        ? `workspace/${this.attr(`workspace-id`)}`
        : `${projectId}`;
      await apiClient.post(`${apiHost()}/report/${workspaceIdOrProjectId}/triggers/pause/`, {
        trigger_ids: messageIds,
      });
    }

    getAffectedMessages(customEventId) {
      const {projectId} = this.state;
      const workspaceId = this.attr(`workspace-id`);
      const workspaceIdOrProjectId = workspaceId ? `workspaces/${workspaceId}` : `projects/${projectId}`;
      // using apiClient.fetch on legacy endpoint with incorrect format
      // eslint-disable-next-line local-rules/deprecated-member-access
      return apiClient
        .fetch(`${apiHost()}/api/app/${workspaceIdOrProjectId}/custom_events/affected/${customEventId}/`)
        .then((response) => response.json())
        .then((response) => response.notif_ids_to_pause)
        .catch(() => []) // swallow errors
        .then((affectedMessageIds) => {
          this.update({affectedMessageIds});
        });
    }
    //#endregion
  },
);
